var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { parseMonth } from "@ui/utils/month";
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { PageHeader } from "../../components/header/PageHeader";
import { NoModuleAccess } from "../../components/no-module-access/NoModuleAccess";
import Button from "@mui/material/Button";
import { Box, Tooltip, useMediaQuery, IconButton, Chip, useTheme, Select, MenuItem, FormControl, InputLabel, Skeleton, Typography, Tabs, Tab, CircularProgress, Checkbox, Card, Stack, Divider, } from "@mui/material";
import { getGridNumericOperators, getGridStringOperators, useGridApiRef, } from "@mui/x-data-grid";
import FilterIcon from "@ui/components/icons/FilterIcon";
import { CardPagination } from "@ui/components/pagination/CardPagination";
import { BDrawer } from "@ui/components/drawer/BDrawer";
import { Moneys, StatusUp, MoneyRecive, MoneyTick, Folder, } from "iconsax-react";
import { FilteredInput } from "@ui/components/inputs";
import { useNavigate, Navigate } from "react-router-dom";
import { ProjectCard } from "../../components/card/ProjectCard";
import { highlightText } from "../../utils/highlightText";
import { buildProjectDetailUrl, getSnapshotRowId, rememberProjectNavList, toProjectNavEntry, } from "../../utils/projectNavigation";
import { smartTruncateString } from "../../utils/smartTruncation";
import { formatDutchNumberWithSuffix, formatDutchNumberWithKSuffixOnly, } from "../../utils/numberFormat";
import { useGetProjectSnapshotsQuery, useSetProjectExclusionMutation, } from "../../../features/projects/projectsApi";
import { useSnackbar } from "notistack";
import { useCompany } from "../../../context/CompanyContext";
import { usePermissions } from "../../../context/PermissionContext";
const SearchSVGIcon = () => (React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z", stroke: "#637381", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
    React.createElement("path", { d: "M21 21L17 17", stroke: "#637381", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })));
const CloseSVGIcon = () => (React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
    React.createElement("path", { d: "M18 6L6 18", stroke: "#637381", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
    React.createElement("path", { d: "M6 6L18 18", stroke: "#637381", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })));
const ChevronDownIcon = ({ className, style, }) => (React.createElement("svg", { className: className, style: style, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", width: "18", height: "18", fill: "none" },
    React.createElement("path", { d: "M6 9L12 15L18 9", stroke: "#637381", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
// Helper function to format total values with negative formatting (red color and brackets)
const formatTotalValue = (value, unitType) => {
    const absValue = Math.abs(value);
    let formattedValue;
    if (unitType === "full") {
        formattedValue = absValue.toLocaleString("nl-NL", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }
    else if (unitType === "M") {
        formattedValue = formatDutchNumberWithSuffix(absValue);
    }
    else {
        formattedValue = formatDutchNumberWithKSuffixOnly(absValue);
    }
    if (value < 0) {
        return React.createElement("span", { style: { color: "red" } },
            "(",
            formattedValue,
            ")");
    }
    return formattedValue;
};
// One metric inside the totals bar: icon, value and label on a single line.
// Kept local rather than folded into MetricCard â€” that card is a stacked tile
// and is still used as such on the project detail page.
const TotalItem = ({ icon, label, value }) => (React.createElement(Stack, { direction: "row", spacing: 1, sx: { alignItems: "center", flex: 1, minWidth: 0, px: { xs: 0, md: 1.5 } } },
    React.createElement(Box, { sx: { display: "inline-flex", flexShrink: 0 } }, icon),
    React.createElement(Typography, { component: "span", sx: {
            fontSize: 16,
            fontWeight: 700,
            color: "#161c24",
            whiteSpace: "nowrap",
        } }, value),
    React.createElement(Typography, { noWrap: true, sx: { fontSize: 13, color: "text.secondary" } }, label)));
// Utility function to format project name with search highlighting and smart truncation
const formatProjectName = (projectName, searchTerm, isSearchResult = false, tooltipPath) => {
    if (!projectName)
        return "N/A";
    const shouldHighlight = isSearchResult && searchTerm && searchTerm.trim() !== "";
    // Use tooltipPath if available, otherwise use the original logic
    const tooltipContent = tooltipPath || projectName;
    // Check if the name contains "..." pattern (parent path)
    const pathMatch = projectName.match(/^(.+\.\.\.)\/(.+)$/);
    if (pathMatch) {
        const [, parentPath, originalName] = pathMatch;
        const fullPath = `${parentPath}/${originalName}`;
        // Apply smart truncation to the full path (string version for highlighting)
        const truncatedPathString = smartTruncateString(fullPath, 100);
        // Apply highlighting to the truncated result
        const highlightedPath = shouldHighlight
            ? highlightText(truncatedPathString, searchTerm)
            : truncatedPathString;
        // Wrap with tooltip showing tooltipPath or full path
        return (React.createElement(Tooltip, { title: tooltipContent, arrow: true },
            React.createElement("span", { style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    width: "100%",
                } }, highlightedPath)));
    }
    // If no path pattern, apply smart truncation and highlighting
    const truncatedNameString = smartTruncateString(projectName, 100);
    const highlightedName = shouldHighlight
        ? highlightText(truncatedNameString, searchTerm)
        : truncatedNameString;
    // Wrap with tooltip showing tooltipPath or project name
    return (React.createElement(Tooltip, { title: tooltipContent, arrow: true },
        React.createElement("span", { style: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                width: "100%",
            } }, highlightedName)));
};
// Filter operators - only use 'contains' operator for strings and '>=' for numbers
const stringFilterOperators = getGridStringOperators().filter((operator) => operator.value === "contains");
const numericFilterOperators = getGridNumericOperators().filter((operator) => operator.value === ">=");
// Custom filter operator for status dropdown
const statusFilterOperators = [
    {
        label: "is",
        value: "is",
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
                return null;
            }
            return (params) => {
                var _a;
                const cellValue = ((_a = params.value) !== null && _a !== void 0 ? _a : "").toString().toLowerCase().trim();
                const filterValue = filterItem.value.toLowerCase().trim();
                return cellValue === filterValue;
            };
        },
        InputComponent: (props) => {
            const { item, applyValue } = props;
            const handleFilterChange = (event) => {
                applyValue(Object.assign(Object.assign({}, item), { value: event.target.value }));
            };
            return (React.createElement(Select, { value: item.value || "", onChange: handleFilterChange, displayEmpty: true, size: "small", sx: { minWidth: 150 } },
                React.createElement(MenuItem, { value: "" },
                    React.createElement("em", null, "Alles")),
                React.createElement(MenuItem, { value: "Nieuw" }, "Nieuw"),
                React.createElement(MenuItem, { value: "Afgerond" }, "Afgerond"),
                React.createElement(MenuItem, { value: "Nog te doen" }, "Nog te doen"),
                React.createElement(MenuItem, { value: "Bulk closed" }, "Bulk closed")));
        },
    },
];
// Function to create columns with search highlighting
const percentColors = (value) => {
    if (value >= 100)
        return { path: "#161c24", trail: "transparent" }; // black â€” complete
    if (value >= 90)
        return { path: "#63B450", trail: "#E7F4E4" }; // green
    if (value >= 70)
        return { path: "#4DB6F6", trail: "#E5F4FD" }; // blue
    if (value >= 40)
        return { path: "#E7B02C", trail: "#FFF4DC" }; // yellow
    return { path: "#D64F3B", trail: "#FBE4DF" }; // red
};
const createColumns = (searchTerm, isSearchResult = false, unitType = "K", onToggleExcluded, canEditExclusion = false) => [
    {
        field: "parentProjectNumber",
        headerName: "Projectnummer",
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        filterOperators: stringFilterOperators,
    },
    {
        field: "projectLeadName",
        headerName: "Projectleider",
        flex: 1.5,
        minWidth: 140,
        sortable: true,
        filterOperators: stringFilterOperators,
        renderCell: (params) => {
            var _a;
            // The data lake has no lead for every project. Say so rather than
            // leaving the cell blank, which reads as a loading or wiring fault.
            const lead = String((_a = params.value) !== null && _a !== void 0 ? _a : "").trim();
            const value = lead || "N/A";
            const content = lead && isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(lead, searchTerm)
                : value;
            return (React.createElement(Tooltip, { title: value, arrow: true, placement: "left" },
                React.createElement("span", { style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                    } }, content)));
        },
    },
    {
        field: "parentProjectDescription",
        headerName: "Projectnaam",
        flex: 5,
        minWidth: 400,
        sortable: true,
        filterOperators: stringFilterOperators,
        renderCell: (params) => formatProjectName(params.value, searchTerm, isSearchResult, params.row.tooltipPath),
    },
    {
        field: "projectStatus",
        headerName: "Status",
        flex: 1.5,
        minWidth: 140,
        sortable: true,
        filterable: true,
        filterOperators: statusFilterOperators,
        renderCell: (params) => {
            var _a, _b, _c, _d;
            const rawValue = (_c = (_a = params.value) !== null && _a !== void 0 ? _a : (_b = params.row) === null || _b === void 0 ? void 0 : _b.projectStatus) !== null && _c !== void 0 ? _c : (_d = params.row) === null || _d === void 0 ? void 0 : _d.ProjectStatus;
            const value = rawValue !== null && rawValue !== void 0 ? rawValue : "";
            const displayValue = value && isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(String(value), searchTerm)
                : value || "â€”";
            // Determine color based on status value
            const getStatusColor = (status) => {
                const statusLower = status.toLowerCase().trim();
                if (statusLower === "afgerond") {
                    return "success"; // Green for completed
                }
                if (statusLower === "nog te doen") {
                    return "warning"; // Yellow for to be done
                }
                if (statusLower === "nieuw") {
                    return "info"; // Blue for new
                }
                return "primary"; // Default
            };
            const STATUS_STYLES = {
                info: { color: "#006C9C", backgroundColor: "#00B8D929" },
                success: { color: "#54801A", backgroundColor: "#86B84529" },
                warning: { color: "#776304", backgroundColor: "#FBE67E66" },
                primary: { color: "#2476A9", backgroundColor: "#3192D026" },
            };
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Status: ${value || "â€”"}` },
                React.createElement(Chip, { label: displayValue, color: getStatusColor(value), variant: "soft", size: "small", sx: Object.assign({ borderRadius: "24px" }, STATUS_STYLES[getStatusColor(value)]) })));
        },
    },
    {
        field: "aanneemsom",
        headerName: "Aanneemsom",
        type: "number",
        flex: 1.5,
        minWidth: 140,
        sortable: true,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            var _a;
            const rawValue = (_a = params.value) !== null && _a !== void 0 ? _a : "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Aanneemsom: ${tooltipValue}` },
                React.createElement("span", { style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                    } }, formattedValue)));
        },
    },
    {
        field: "forecastMargin",
        headerName: "Forecast Marge",
        type: "number",
        flex: 1,
        minWidth: 150,
        sortable: true,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            // Convert value to number if possible
            const value = params.value;
            const num = typeof value === "string"
                ? parseFloat(value.replace(/[^\d.]/g, ""))
                : value;
            let display = value;
            const absNum = Math.abs(num);
            if (!isNaN(num)) {
                // Format with K/M suffixes using Dutch convention or full number
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            // Format negative values
            const displayText = !isNaN(num) && num < 0 ? `(${String(display)})` : String(display);
            const chipLabel = isSearchResult && searchTerm && searchTerm.trim() !== ""
                ? highlightText(displayText, searchTerm)
                : displayText;
            // Format tooltip value based on unit type
            const tooltipValue = !isNaN(num)
                ? unitType === "full"
                    ? absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })
                    : unitType === "M"
                        ? formatDutchNumberWithSuffix(absNum)
                        : formatDutchNumberWithKSuffixOnly(absNum)
                : value;
            const tooltipDisplay = !isNaN(num) && num < 0 ? `(${tooltipValue})` : tooltipValue;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Forecast Marge: ${tooltipDisplay}` },
                React.createElement(Chip, { label: chipLabel, color: "primary", variant: "soft", size: "small", sx: Object.assign({ borderRadius: "24px" }, (isNaN(num) || num >= 0
                        ? {}
                        : {
                            color: "error.main",
                            "& .MuiChip-label": {
                                color: "error.main",
                            },
                        })) })));
        },
    },
    {
        field: "forecastMarginPercent",
        headerName: "%Forecast Marge",
        flex: 1,
        minWidth: 160,
        sortable: true,
        filterable: true,
        filterOperators: numericFilterOperators,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
            const percent = Number(params.value) || 0;
            // Plain number rather than a ring: a 0-100 "fullness" arc implies 100%
            // is the target, which is never true for a margin. Colour still comes
            // from the shared thresholds so it matches POC%.
            // percentColors reserves >= 100 for "complete" (black), which only
            // means something for POC%. A margin at or above 100 is just a very
            // good margin, so hold it in the green band.
            const { path } = percentColors(percent >= 100 ? 90 : percent);
            const formattedPercent = percent.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return (React.createElement(Typography, { component: "span", sx: { fontSize: 14, fontWeight: 600, color: path } },
                formattedPercent,
                "%"));
        },
    },
    {
        field: "afgerondPercent",
        headerName: "POC %",
        flex: 1,
        minWidth: 100,
        sortable: true,
        filterOperators: numericFilterOperators,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
            var _a, _b, _c;
            const valuationLevel = (_a = params.row) === null || _a === void 0 ? void 0 : _a.valuationLevel;
            const hierarchyLevel = (_c = (_b = params.row) === null || _b === void 0 ? void 0 : _b.hierarchyLevel) !== null && _c !== void 0 ? _c : 1;
            if (valuationLevel != null &&
                valuationLevel !== 1 &&
                hierarchyLevel === 1) {
                return (React.createElement(Tooltip, { arrow: true, placement: "left", title: "POC% is alleen zichtbaar op waarderingsniveau 1." },
                    React.createElement(Box, { sx: {
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            cursor: "help",
                        } },
                        React.createElement(Typography, { sx: {
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                color: "text.disabled",
                                letterSpacing: "0.06em",
                                lineHeight: 1,
                            } }, "NB"))));
            }
            const percent = Number(params.value) || 0;
            const { path } = percentColors(percent);
            const formattedPercent = percent.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return (React.createElement(Typography, { component: "span", sx: { fontSize: 14, fontWeight: 600, color: path } },
                formattedPercent,
                "%"));
        },
    },
    {
        field: "gefactureerdPTD",
        headerName: "Gefactureerd PTD",
        type: "number",
        flex: 0.9,
        minWidth: 160,
        sortable: true,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Gefactureerd PTD: ${tooltipValue}` },
                React.createElement("span", { style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                    } }, formattedValue)));
        },
    },
    {
        field: "kostenPTD",
        headerName: "Kosten PTD",
        type: "number",
        flex: 0.9,
        minWidth: 120,
        sortable: true,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `Kosten PTD: ${tooltipValue}` },
                React.createElement("span", { style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                    } }, formattedValue)));
        },
    },
    {
        field: "ohw",
        headerName: "OHW",
        type: "number",
        flex: 0.6,
        minWidth: 140,
        sortable: true,
        filterable: true,
        headerAlign: "left",
        align: "left",
        filterOperators: numericFilterOperators,
        renderCell: (params) => {
            const rawValue = params.value || "";
            const num = Number(rawValue);
            const absNum = Math.abs(num);
            // Format with K/M suffixes using Dutch convention or full number
            let display = rawValue;
            if (!isNaN(num)) {
                if (unitType === "full") {
                    display = absNum.toLocaleString("nl-NL", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    });
                }
                else if (unitType === "M") {
                    display = formatDutchNumberWithSuffix(absNum);
                }
                else {
                    // Default to K
                    display = formatDutchNumberWithKSuffixOnly(absNum);
                }
            }
            const displayValue = isSearchResult && (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim())
                ? highlightText(String(display), searchTerm)
                : display;
            // Apply negative formatting with brackets
            const formattedValue = !isNaN(num) && num < 0 ? (React.createElement("span", { style: { color: "red" } },
                "(",
                displayValue,
                ")")) : (displayValue);
            // Format tooltip with same display format
            const tooltipValue = !isNaN(num) && num < 0 ? `(${display})` : display;
            return (React.createElement(Tooltip, { arrow: true, placement: "left", title: `OHW: ${tooltipValue}` },
                React.createElement("span", { style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        width: "100%",
                    } }, formattedValue)));
        },
    },
    {
        field: "directChildrenCount",
        headerName: "# Deelprojecten",
        flex: 1.5,
        minWidth: 140,
        sortable: false,
        filterOperators: stringFilterOperators,
    },
    {
        field: "isExcluded",
        headerName: "Niet waarderen",
        flex: 1,
        minWidth: 110,
        sortable: false,
        filterable: true,
        filterOperators: statusFilterOperators,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
            var _a, _b;
            if ((_a = params.row) === null || _a === void 0 ? void 0 : _a.isTotals)
                return null;
            const isExcluded = !!((_b = params.row) === null || _b === void 0 ? void 0 : _b.isExcluded);
            return (React.createElement(Tooltip, { title: isExcluded
                    ? "Project weer waarderen in het volledige OHW-proces"
                    : "Project niet waarderen in het volledige OHW-proces", arrow: true },
                React.createElement("span", null,
                    React.createElement(Checkbox, { checked: isExcluded, disabled: !canEditExclusion, onClick: (event) => event.stopPropagation(), onChange: (event) => onToggleExcluded === null || onToggleExcluded === void 0 ? void 0 : onToggleExcluded(params.row, event.target.checked) }))));
        },
    },
];
export const Home = () => {
    var _a;
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [page, setPage] = React.useState(() => Number(sessionStorage.getItem("project-page")) || 0);
    const [pageSize, setPageSize] = React.useState(() => Number(sessionStorage.getItem("project-pageSize")) || 10);
    const [snapshotsData, setSnapshotsData] = React.useState([]);
    const [totalRowCount, setTotalRowCount] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState(() => sessionStorage.getItem("project-search") || "");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState(() => sessionStorage.getItem("project-search") || "");
    const [aanneemsom, setAanneemsom] = React.useState(() => sessionStorage.getItem("project-aanneemsom") || "");
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [debouncedAanneemsom, setDebouncedAanneemsom] = React.useState(() => sessionStorage.getItem("project-aanneemsom") || "");
    const [unitType, setUnitType] = React.useState(() => { var _a; return (_a = localStorage.getItem("project-unitType")) !== null && _a !== void 0 ? _a : "full"; });
    const handleUnitTypeChange = React.useCallback((value) => {
        localStorage.setItem("project-unitType", value);
        setUnitType(value);
    }, []);
    const getStoredFilterModel = () => {
        try {
            const stored = sessionStorage.getItem("project-filterModel");
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && Array.isArray(parsed.items)) {
                    return parsed;
                }
            }
        }
        catch (_a) {
            // Invalid JSON, fall back to empty model
        }
        return { items: [] };
    };
    const computeFilterValuesKey = (model) => {
        var _a;
        return ((_a = model.items) === null || _a === void 0 ? void 0 : _a.filter((item) => item.value !== undefined && item.value !== null && item.value !== "").map((item) => `${item.field}:${String(item.value)}`).sort().join("|")) || "";
    };
    const [filterModel, setFilterModel] = React.useState(getStoredFilterModel);
    const [debouncedFilterModel, setDebouncedFilterModel] = React.useState(getStoredFilterModel);
    const previousFilterValuesRef = useRef(computeFilterValuesKey(getStoredFilterModel()));
    const isInitialSearchRef = useRef(true);
    const isInitialAanneemsomRef = useRef(true);
    const gridWrapperRef = useRef(null);
    // The wrapper only mounts once permissions have loaded (a spinner renders first), so a
    // mount-only effect saw a null ref and never measured. Track the element in state so the
    // measuring effect runs when it actually appears.
    const [gridWrapperEl, setGridWrapperEl] = useState(null);
    const setGridWrapper = React.useCallback((node) => {
        gridWrapperRef.current = node;
        setGridWrapperEl(node);
    }, []);
    // How tall the grid card should be so it ends at the bottom of the viewport.
    //
    // This used to be a hand-tuned "calc(100dvh - 327px)", where the constant
    // stood for the height of the chrome above the grid. Every change to that
    // chrome silently invalidated it and left either a band of dead space under
    // the card or a scrollbar â€” the previous comment recorded one such re-tune,
    // and moving the Forecast Data Month chip onto the title line invalidated it
    // again. Measuring where the card actually starts costs a ResizeObserver and
    // is right by construction.
    //
    // Starts at the old constant so the first paint matches today's layout, and
    // falls back to it if the element is never measured.
    const [gridHeight, setGridHeight] = useState("calc(100dvh - 327px)");
    React.useLayoutEffect(() => {
        const el = gridWrapperEl;
        if (!el)
            return;
        const measure = () => {
            // getBoundingClientRect is viewport-relative, which is what 100dvh is
            // measured against. The shell above is overflow:hidden at 100dvh, so the
            // card's top does not move once laid out: sizing it to exactly fill the
            // remainder keeps the scroll container from scrolling, which keeps the
            // measurement stable rather than feeding back into itself.
            const top = el.getBoundingClientRect().top;
            // Matches the Container's own bottom padding, so the card stops just
            // short of the edge rather than butting against it.
            const next = Math.round(window.innerHeight - top - 12);
            // A floor keeps the no-rows and skeleton overlays legible if the chrome
            // ever grows tall enough to squeeze the card.
            setGridHeight(`${Math.max(260, next)}px`);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(document.body);
        // body is a fixed 100dvh, so it never resizes when chrome above the card changes
        // (e.g. the "filters toegepast" row appearing after a search). The card's parent
        // holds that chrome, so its height does change — re-measure then, or the card keeps
        // its old height, overflows the Layout scroller and shows a second scrollbar.
        if (el.parentElement)
            observer.observe(el.parentElement);
        window.addEventListener("resize", measure);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [gridWrapperEl]);
    const hasRestoredScrollRef = useRef(false);
    const gridApiRef = useGridApiRef();
    const [sortModel, setSortModel] = React.useState(() => {
        try {
            const stored = sessionStorage.getItem("project-sortModel");
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            }
        }
        catch (_a) {
            // Invalid JSON, fall back to no sort
        }
        return [];
    });
    const isSortingRef = useRef(false);
    // Start with loading state true to show skeleton immediately on mount
    const [isSearching, setIsSearching] = React.useState(true);
    const navigate = useNavigate();
    const [setProjectExclusion] = useSetProjectExclusionMutation();
    const [totalProjectsCount, setTotalProjectsCount] = React.useState(null);
    const [newProjectsCount, setNewProjectsCount] = React.useState(null);
    const [inProgressProjectsCount, setInProgressProjectsCount] = React.useState(null);
    const [bulkClosedProjectsCount, setBulkClosedProjectsCount] = React.useState(null);
    const [afgerondProjectsCount, setAfgerondProjectsCount] = React.useState(null);
    const [excludedProjectsCount, setExcludedProjectsCount] = React.useState(null);
    const [totalsRow, setTotalsRow] = React.useState(null);
    // Tab state for status filtering
    const [statusTab, setStatusTab] = React.useState("all");
    // Sync tab state with filter model (only when filter changes externally, not from tab clicks)
    const isTabChangeRef = useRef(false);
    // When handleToggleExcluded last patched the counts/totals locally. Every tab
    // is its own cache entry, so "have I edited since" is not a single flag: a
    // settled response for the tab you are on says nothing about the entries
    // behind the other tabs, which are still older than the toggle. Comparing
    // each response's own fulfilledTimeStamp against this marker is what actually
    // answers "is what I am about to paint older than what is on screen".
    const countsDirtySinceRef = useRef(0);
    // Handle status tab changes
    const handleStatusTabChange = (event, newValue) => {
        isTabChangeRef.current = true;
        setStatusTab(newValue);
        setSearchTerm("");
        if (newValue === "all") {
            setFilterModel({ items: [] });
            setDebouncedFilterModel({ items: [] });
            previousFilterValuesRef.current = "";
            setPage(0);
        }
        else {
            if (newValue === "excluded") {
                const newFilterModel = {
                    items: [
                        {
                            id: "status-tab-excluded",
                            field: "isExcluded",
                            operator: "is",
                            value: "true",
                        },
                    ],
                };
                setFilterModel(newFilterModel);
                setDebouncedFilterModel(newFilterModel);
                previousFilterValuesRef.current = "isExcluded:true";
                setPage(0);
                return;
            }
            const statusMap = {
                new: "Nieuw",
                afgerond: "Afgerond",
                inprogress: "Nog te doen",
                bulkclosed: "Bulk closed",
            };
            const newFilterModel = {
                items: [
                    {
                        id: `status-tab-${newValue}`,
                        field: "projectStatus",
                        operator: "is",
                        value: statusMap[newValue],
                    },
                ],
            };
            setFilterModel(newFilterModel);
            setDebouncedFilterModel(newFilterModel);
            previousFilterValuesRef.current = `projectStatus:${statusMap[newValue]}`;
            setPage(0);
        }
    };
    const handleToggleExcluded = (row, nextValue) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const parentProjectNumber = (_a = row === null || row === void 0 ? void 0 : row.topParentProjectNumber) !== null && _a !== void 0 ? _a : row === null || row === void 0 ? void 0 : row.parentProjectNumber;
        const companyId = selectedCompanyId || getSelectedCompanyIds()[0] || "";
        if (!parentProjectNumber || !companyId)
            return;
        if (!canUpdate("Projects")) {
            enqueueSnackbar("U heeft geen toestemming om dit project uit te sluiten", {
                variant: "error",
            });
            return;
        }
        // Everything below is applied before the request, not after it. The
        // checkbox is controlled by the row's own isExcluded, so awaiting the round
        // trip first left the user's click springing visibly back to its old
        // position for the duration of the call before the row finally vanished.
        // On failure the catch resyncs from the server instead of unpicking each
        // patch by hand.
        try {
            // The row no longer belongs in the current view once its excluded state
            // flips (excluded projects are hidden by default, shown only on the
            // "Uitgesloten" tab), so drop it locally instead of refetching.
            setSnapshotsData((prev) => prev.filter((p) => {
                var _a;
                return ((_a = p.topParentProjectNumber) !== null && _a !== void 0 ? _a : p.parentProjectNumber) !==
                    parentProjectNumber;
            }));
            setTotalRowCount((prev) => Math.max(0, prev - 1));
            // The toggled row's contribution must come out of the visible totals too
            // (Totaal Aanneemsom, Forecast Marge, Gefactureerd/Kosten PTD, OHW) â€”
            // otherwise those metric cards keep counting a project that no longer
            // belongs to the current view until the next full refetch.
            // Subtracting floats from a server-computed sum leaves dust (e.g.
            // -0.0000004 once the last row of a tab is toggled off), and every
            // renderer here treats `value < 0` as "negative" â€” so that dust shows up
            // as a red "(0)". Snap to cents so an emptied total lands on a clean 0.
            const subtract = (total, part) => Math.round(((total || 0) - (part || 0)) * 100) / 100 || 0;
            setTotalsRow((prev) => {
                if (!prev)
                    return prev;
                return Object.assign(Object.assign({}, prev), { aanneemsom: subtract(prev.aanneemsom, row === null || row === void 0 ? void 0 : row.aanneemsom), forecastMargin: subtract(prev.forecastMargin, row === null || row === void 0 ? void 0 : row.forecastMargin), gefactureerdPTD: subtract(prev.gefactureerdPTD, row === null || row === void 0 ? void 0 : row.gefactureerdPTD), kostenPTD: subtract(prev.kostenPTD, row === null || row === void 0 ? void 0 : row.kostenPTD), ohw: subtract(prev.ohw, row === null || row === void 0 ? void 0 : row.ohw) });
            });
            // Excluding removes the project from its status bucket's count (and the
            // overall total); un-excluding adds it back to whichever bucket it
            // actually belongs to. Without this, the status pills go stale relative
            // to the Excluded pill even though the grid itself updates correctly.
            const delta = nextValue ? -1 : 1;
            const bump = (prev) => Math.max(0, (prev !== null && prev !== void 0 ? prev : 0) + delta);
            // Everything patched below is now ahead of every cached response.
            countsDirtySinceRef.current = Date.now();
            setExcludedProjectsCount((prev) => Math.max(0, (prev !== null && prev !== void 0 ? prev : 0) - delta));
            setTotalProjectsCount(bump);
            const status = row === null || row === void 0 ? void 0 : row.projectStatus;
            if ((row === null || row === void 0 ? void 0 : row.isBulkClosed) || status === "Massa afgerond") {
                setBulkClosedProjectsCount(bump);
            }
            else if (status === "Afgerond") {
                setAfgerondProjectsCount(bump);
            }
            else if (status === "Nog te doen") {
                setInProgressProjectsCount(bump);
            }
            else if (status === "Nieuw") {
                setNewProjectsCount(bump);
            }
            yield setProjectExclusion({
                companyId,
                parentProjectNumber,
                isExcluded: nextValue,
            }).unwrap();
        }
        catch (_b) {
            enqueueSnackbar("Wijzigen van 'Niet waarderen' is mislukt", {
                variant: "error",
            });
            // Nothing was persisted, so the local patches above are the wrong answer
            // now. Drop the marker first â€” otherwise the aggregate gate below would
            // reject the very response fetched to undo them â€” then resync.
            countsDirtySinceRef.current = 0;
            refetchSnapshots();
        }
    });
    React.useEffect(() => {
        var _a, _b;
        if (isTabChangeRef.current) {
            isTabChangeRef.current = false;
            return;
        }
        const excludedFilter = (_a = filterModel.items) === null || _a === void 0 ? void 0 : _a.find((item) => item.field === "isExcluded" && item.operator === "is");
        if (excludedFilter) {
            if (statusTab !== "excluded") {
                setStatusTab("excluded");
            }
            return;
        }
        const statusFilter = (_b = filterModel.items) === null || _b === void 0 ? void 0 : _b.find((item) => item.field === "projectStatus" && item.operator === "is");
        if (!statusFilter) {
            if (statusTab !== "all") {
                setStatusTab("all");
            }
        }
        else {
            const statusValue = statusFilter.value;
            const tabMap = {
                Nieuw: "new",
                Afgerond: "afgerond",
                "Nog te doen": "inprogress",
                "Bulk closed": "bulkclosed",
            };
            const expectedTab = tabMap[statusValue] || "all";
            if (statusTab !== expectedTab) {
                setStatusTab(expectedTab);
            }
        }
    }, [filterModel]);
    // Reset loading state when component unmounts to prevent state persistence
    useEffect(() => {
        return () => {
            setIsSearching(false);
            try {
                // Hide any DataGrid loading overlays that might persist - don't remove to avoid React removeChild errors
                const dataGridOverlays = document.querySelectorAll(".MuiDataGrid-overlayWrapper, .MuiDataGrid-overlay, .MuiDataGrid-loadingOverlay");
                dataGridOverlays.forEach((overlay) => {
                    try {
                        const overlayElement = overlay;
                        // Just hide it - don't remove to avoid React removeChild errors
                        overlayElement.style.setProperty("display", "none", "important");
                        overlayElement.style.setProperty("pointer-events", "none", "important");
                        overlayElement.style.setProperty("z-index", "-1", "important");
                    }
                    catch (_a) {
                        // Silently ignore errors for individual elements
                    }
                });
                // Hide any backdrops - don't remove to avoid React removeChild errors
                const backdrops = document.querySelectorAll(".MuiBackdrop-root");
                backdrops.forEach((backdrop) => {
                    try {
                        const backdropElement = backdrop;
                        // Only hide if it's not part of an active dialog/modal
                        const parent = backdropElement.parentElement;
                        const isActiveDialog = (parent === null || parent === void 0 ? void 0 : parent.classList.contains("MuiModal-root")) ||
                            (parent === null || parent === void 0 ? void 0 : parent.classList.contains("MuiDialog-root"));
                        if (!isActiveDialog) {
                            // Just hide it - don't remove to avoid React removeChild errors
                            backdropElement.style.setProperty("display", "none", "important");
                            backdropElement.style.setProperty("pointer-events", "none", "important");
                            backdropElement.style.setProperty("z-index", "-1", "important");
                        }
                    }
                    catch (_a) {
                        // Silently ignore errors for individual elements
                    }
                });
            }
            catch (_a) {
                // Silently ignore cleanup errors
            }
        };
    }, []);
    const { enqueueSnackbar } = useSnackbar();
    const { canRead, canUpdate, isLoading: isLoadingPermissions, isAuthError: isPermissionAuthError, } = usePermissions();
    // Get selected companies from context
    const { getSelectedCompanyNames, getSelectedCompanyIds, selectedCompanyId, companies, isLoadingCompanies, hasPendingChanges, companyMonthConfigs, } = useCompany();
    // Use a ref to track previous config to avoid unnecessary refetches
    const prevConfigRef = React.useRef("");
    // Create a stable reference to config month to detect actual changes
    const configMonthKey = React.useMemo(() => {
        if (!companyMonthConfigs || companyMonthConfigs.length === 0) {
            return "";
        }
        return JSON.stringify(companyMonthConfigs
            .map((c) => ({ companyId: c.companyId, configMonth: c.configMonth }))
            .sort((a, b) => a.companyId.localeCompare(b.companyId)));
    }, [companyMonthConfigs]);
    // Track config changes separately to avoid multiple calls
    React.useEffect(() => {
        if (prevConfigRef.current !== configMonthKey &&
            prevConfigRef.current !== "") {
            // Config actually changed, trigger refetch by resetting page
            setPage(0);
        }
        prevConfigRef.current = configMonthKey;
    }, [configMonthKey]);
    // Persist pagination, search, filters and scroll position for the session so
    // they survive navigating away and back (e.g. opening a project and returning
    // to the list).
    useEffect(() => {
        sessionStorage.setItem("project-page", String(page));
    }, [page]);
    useEffect(() => {
        sessionStorage.setItem("project-pageSize", String(pageSize));
    }, [pageSize]);
    useEffect(() => {
        sessionStorage.setItem("project-search", searchTerm);
    }, [searchTerm]);
    useEffect(() => {
        sessionStorage.setItem("project-aanneemsom", aanneemsom);
    }, [aanneemsom]);
    useEffect(() => {
        sessionStorage.setItem("project-filterModel", JSON.stringify(filterModel));
    }, [filterModel]);
    useEffect(() => {
        sessionStorage.setItem("project-sortModel", JSON.stringify(sortModel));
    }, [sortModel]);
    // Handle sort model changes and prevent page reset
    const handleSortModelChange = React.useCallback((newModel) => {
        isSortingRef.current = true;
        setSortModel(newModel);
        // Reset flag after a short delay to allow pagination callback to fire
        setTimeout(() => {
            isSortingRef.current = false;
        }, 100);
    }, []);
    // Clear all filters and search
    const handleClearAll = () => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
        setAanneemsom("");
        setDebouncedAanneemsom("");
        setFilterModel({ items: [] });
        setDebouncedFilterModel({ items: [] });
        previousFilterValuesRef.current = "";
        setPage(0);
        sessionStorage.removeItem("project-search");
        sessionStorage.removeItem("project-aanneemsom");
        sessionStorage.removeItem("project-filterModel");
        sessionStorage.removeItem("project-scrollTop");
    };
    // Debounce search term
    useEffect(() => {
        if (isInitialSearchRef.current) {
            // Skip resetting the page on the first render â€” searchTerm was hydrated
            // from sessionStorage together with the page/pageSize it belongs to.
            isInitialSearchRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(0); // Reset to first page when search changes
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Debounce aanneemsom filter
    useEffect(() => {
        if (isInitialAanneemsomRef.current) {
            isInitialAanneemsomRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedAanneemsom(aanneemsom);
            setPage(0); // Reset to first page when filter changes
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [aanneemsom]);
    // Debounce filter model changes - only trigger when there's actual filter values being typed
    useEffect(() => {
        var _a;
        // Extract current filter values as a string for comparison
        const currentFilterValues = ((_a = filterModel.items) === null || _a === void 0 ? void 0 : _a.filter((item) => item.value !== undefined && item.value !== null && item.value !== "").map((item) => `${item.field}:${String(item.value)}`).sort().join("|")) || "";
        // Only trigger debounce if values actually changed (user typed something)
        // If current values are empty and previous were empty, don't trigger (just opening/closing filter)
        if (currentFilterValues === previousFilterValuesRef.current) {
            return;
        }
        // If clearing filters (empty string), update immediately without debounce
        if (currentFilterValues === "" && previousFilterValuesRef.current !== "") {
            previousFilterValuesRef.current = "";
            setDebouncedFilterModel(filterModel);
            setPage(0);
            return;
        }
        // If no filter values, skip debounce
        if (currentFilterValues === "") {
            return;
        }
        // Update the ref immediately to track the change
        previousFilterValuesRef.current = currentFilterValues;
        const timer = setTimeout(() => {
            // Allow update if:
            // 1. There are filter values (user is filtering)
            // 2. OR current values are empty BUT we previously had filters (user is clearing)
            if (currentFilterValues !== "") {
                setDebouncedFilterModel(filterModel);
                setPage(0);
            }
        }, 2000); // 2000ms delay
        return () => clearTimeout(timer);
    }, [filterModel]);
    // Build the snapshots request for the current search/filter/sort/page state.
    // Memoised rather than fired from an effect: RTK Query keys its cache on the
    // request, so an identical one (back to the overview, a filter cleared) renders
    // the rows it already holds and revalidates in the background. null = don't ask yet.
    const snapshotRequest = React.useMemo(() => {
        // Skip API call if there are pending company changes (waiting for redirect)
        if (hasPendingChanges) {
            return null;
        }
        // Get selected company names - try from context first, then localStorage as fallback
        let selectedCompanyNames = getSelectedCompanyNames();
        // If no selection from context, try localStorage immediately (don't wait for companies to load)
        if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
            const stored = localStorage.getItem("selectedCompanies");
            if (stored) {
                try {
                    selectedCompanyNames = JSON.parse(stored);
                }
                catch (_a) {
                    // Invalid JSON, ignore
                }
            }
        }
        // Skip API call if no companies are selected
        if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
            return null;
        }
        const filters = [];
        // Define numeric fields (must match backend switch cases)
        const numericFields = [
            "aanneemsom",
            "omzet",
            "ohw",
            "afgerondpercent",
            "forecastmargin",
            "forecastmarginpercent",
        ];
        // Map MUI DataGrid operators to backend operators
        const mapOperator = (muiOperator, field) => {
            if (!muiOperator) {
                // Default operator based on field type
                return numericFields.includes(field) ? "gte" : "contains";
            }
            // Map MUI operator names to backend operator names
            // MUI DataGrid uses camelCase operator names
            const operatorMap = {
                // Symbol operators (if used)
                "=": "eq",
                "!=": "neq",
                ">": "gt",
                ">=": "gte",
                "<": "lt",
                "<=": "lte",
                // CamelCase operators (standard MUI DataGrid format)
                equals: "eq",
                notEquals: "neq",
                greaterThan: "gt",
                greaterThanOrEqual: "gte",
                lessThan: "lt",
                lessThanOrEqual: "lte",
                contains: "contains",
                startsWith: "startsWith",
                endsWith: "endsWith",
                // Already in correct format
                eq: "eq",
                neq: "neq",
                gt: "gt",
                gte: "gte",
                lt: "lt",
                lte: "lte",
            };
            // Normalize operator (lowercase for case-insensitive matching)
            const normalizedOperator = muiOperator.toLowerCase();
            return (operatorMap[normalizedOperator] ||
                operatorMap[muiOperator] ||
                muiOperator);
        };
        if (debouncedFilterModel.items) {
            debouncedFilterModel.items.forEach((filterItem) => {
                if (filterItem.value !== undefined &&
                    filterItem.value !== null &&
                    filterItem.value !== "") {
                    // Use the actual operator from filterItem, mapped to backend format
                    const operator = mapOperator(filterItem.operator, filterItem.field);
                    filters.push({
                        field: filterItem.field,
                        operator_: operator,
                        value: String(filterItem.value),
                    });
                }
            });
        }
        // Note: projectDescriptionSearch is now passed separately, not as a filter
        // Add aanneemsom filter if provided
        if (debouncedAanneemsom && debouncedAanneemsom.trim() !== "") {
            filters.push({
                field: "aanneemsom",
                operator_: "contains",
                value: debouncedAanneemsom,
            });
        }
        // Build pagination object (convert from 0-based to 1-based)
        const pagination = {
            page: page + 1, // Convert from 0-based to 1-based
            pageSize,
        };
        // Map grid field names to backend sort keys (must match the switch in
        // ProjectDataRepository.cs â€” anything not in this map is passed through
        // as-is since the backend already lowercases the field name).
        const sortFieldMap = {
            gefactureerdPTD: "gefactureerd",
            kostenPTD: "kosten",
        };
        const sorting = sortModel.map((item) => ({
            field: sortFieldMap[item.field] || item.field,
            sort: item.sort || "asc",
        }));
        // Get company ID - find by selected company name
        // Use selectedCompanyNames from above (may come from localStorage or context)
        let companyId = "";
        // Try to find companyId from companies array by matching entiteitNaam
        if (companies && companies.length > 0 && selectedCompanyNames.length > 0) {
            const selectedName = selectedCompanyNames[0];
            const company = companies.find((c) => c.entiteitNaam === selectedName);
            companyId = (company === null || company === void 0 ? void 0 : company.entiteitId) || "";
            // If still not found, try getSelectedCompanyIds as fallback
            if (!companyId) {
                const companyIds = getSelectedCompanyIds();
                companyId = companyIds.length > 0 ? companyIds[0] : "";
            }
        }
        // Also try selectedCompanyId from context as last resort
        if (!companyId && selectedCompanyId) {
            companyId = selectedCompanyId;
        }
        // CompanyId is required for calculations - wait for it to be available
        // (the skeleton stays up via waitingForCompanyId)
        if (!companyId) {
            return null;
        }
        // Build request object matching backend structure
        const request = Object.assign(Object.assign(Object.assign({ entiteitNaam: selectedCompanyNames, pagination,
            companyId }, (filters.length > 0 ? { filters } : {})), (sorting.length > 0 ? { sorting } : {})), (debouncedSearchTerm && debouncedSearchTerm.trim() !== ""
            ? { projectDescriptionSearch: debouncedSearchTerm.trim() }
            : {}));
        return request;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasPendingChanges,
        debouncedSearchTerm,
        debouncedAanneemsom,
        debouncedFilterModel,
        sortModel,
        page,
        pageSize,
        companies,
        selectedCompanyId,
    ]);
    const { data: snapshotsResult, isLoading: snapshotsLoading, fulfilledTimeStamp: snapshotsFulfilledAt, isError: snapshotsFailed, refetch: refetchSnapshots, } = useGetProjectSnapshotsQuery(snapshotRequest !== null && snapshotRequest !== void 0 ? snapshotRequest : skipToken, {
        // Always revalidate, but cached rows stay on screen while it runs.
        refetchOnMountOrArgChange: true,
    });
    // Map the cached/fetched response onto the grid's state.
    useEffect(() => {
        if (!snapshotsResult)
            return;
        // Nothing from a response older than the last exclusion toggle is used â€”
        // not the rows, not the counts, not the totals.
        //
        // Each tab is its own request, so RTK serves that tab's previous cached
        // response and refetches in the background. After a toggle, that cached
        // response describes a world that no longer exists: its rows still contain
        // the project you just excluded and its statusCounts/totals still count it.
        // Painting any of it means showing a stale answer and correcting it a
        // moment later â€” first seen as pills reverting on a tab switch, then as the
        // row list changing under the user once the pills were fixed.
        //
        // Show the loader for that gap instead. With nothing toggled the marker is
        // 0, every cached response passes, and a tab switch still paints instantly
        // from cache â€” the fast path is untouched.
        if ((snapshotsFulfilledAt !== null && snapshotsFulfilledAt !== void 0 ? snapshotsFulfilledAt : 0) < countsDirtySinceRef.current) {
            setIsSearching(true);
            return;
        }
        // Normalize casing (backend might return ProjectStatus vs projectStatus)
        const normalizedRows = (snapshotsResult.data || []).map((row) => {
            var _a, _b, _c, _d, _e;
            return (Object.assign(Object.assign({}, row), { projectStatus: (_a = row === null || row === void 0 ? void 0 : row.projectStatus) !== null && _a !== void 0 ? _a : row === null || row === void 0 ? void 0 : row.ProjectStatus, gefactureerdPTD: (_c = (_b = row === null || row === void 0 ? void 0 : row.gefactureerd) !== null && _b !== void 0 ? _b : row === null || row === void 0 ? void 0 : row.Gefactureerd) !== null && _c !== void 0 ? _c : 0, kostenPTD: (_e = (_d = row === null || row === void 0 ? void 0 : row.kosten) !== null && _d !== void 0 ? _d : row === null || row === void 0 ? void 0 : row.Kosten) !== null && _e !== void 0 ? _e : 0 }));
        });
        setSnapshotsData(normalizedRows);
        setTotalRowCount(snapshotsResult.totalCount);
        // Set KPIs and totals from backend response statusCounts object
        if (snapshotsResult.statusCounts) {
            setTotalProjectsCount(snapshotsResult.statusCounts.all);
            setNewProjectsCount(snapshotsResult.statusCounts.nieuwCount);
            setInProgressProjectsCount(snapshotsResult.statusCounts.nogTeDoenCount);
            setBulkClosedProjectsCount(snapshotsResult.statusCounts.bulkClosedCount);
            setAfgerondProjectsCount(snapshotsResult.statusCounts.afgerondCount);
            setExcludedProjectsCount(snapshotsResult.statusCounts.excludedCount);
        }
        // Set totals row from backend response
        const totals = snapshotsResult.totals;
        if (totals) {
            setTotalsRow({
                isTotals: true,
                parentProjectNumber: "Totaal",
                aanneemsom: totals.aanneemsom || 0,
                forecastMargin: totals.forecastMargin || 0,
                gefactureerdPTD: totals.gefactureerd || totals.Gefactureerd || 0,
                kostenPTD: totals.kosten || totals.Kosten || 0,
                ohw: totals.ohw || 0,
            });
        }
        else {
            setTotalsRow(null);
        }
        // Cleared here rather than only in the waitingForCompanyId effect below, so a
        // legitimately empty result also takes the skeleton down.
        setIsSearching(false);
        // snapshotsFulfilledAt is a dependency, not just a read: a refetch that
        // returns identical data still has to re-run this effect so the aggregates
        // it skipped get applied.
    }, [snapshotsResult, snapshotsFulfilledAt]);
    useEffect(() => {
        if (!snapshotsFailed)
            return;
        setSnapshotsData([]);
        setTotalRowCount(0);
        setIsSearching(false);
        enqueueSnackbar("Fout bij het ophalen van projectgegevens", {
            variant: "error",
        });
    }, [snapshotsFailed, enqueueSnackbar]);
    // No company selected while companies are already loaded: nothing to show.
    useEffect(() => {
        if (snapshotRequest === null &&
            !hasPendingChanges &&
            !isLoadingCompanies &&
            companies &&
            companies.length > 0) {
            setSnapshotsData([]);
            setTotalRowCount(0);
        }
    }, [snapshotRequest, hasPendingChanges, isLoadingCompanies, companies]);
    // Determine if we're showing search results (only when there's a search term and we're not loading)
    const hasSearchTerm = debouncedSearchTerm
        ? debouncedSearchTerm.trim() !== ""
        : false;
    const isSearchResult = hasSearchTerm;
    // Check if we're waiting for companyId (required for calculations)
    // Get selected company names - try from context first, then localStorage as fallback
    let selectedCompanyNames = getSelectedCompanyNames();
    if (!selectedCompanyNames || selectedCompanyNames.length === 0) {
        const stored = localStorage.getItem("selectedCompanies");
        if (stored) {
            try {
                selectedCompanyNames = JSON.parse(stored);
            }
            catch (_b) {
                // Invalid JSON, ignore
            }
        }
    }
    // Determine if we're waiting - show skeleton in these cases:
    // 1. Companies are still loading (initial load or incognito)
    // 2. Company is selected but companyId is not available yet
    // 3. No company selected yet but companies are loading (will auto-select)
    let waitingForCompanyId = false;
    const hasSelectedCompany = selectedCompanyNames && selectedCompanyNames.length > 0;
    if (isLoadingCompanies) {
        // Always show skeleton while companies are loading (handles incognito case)
        waitingForCompanyId = true;
    }
    else if (hasSelectedCompany) {
        // Check if we have companyId available for selected company
        let hasCompanyId = false;
        if (companies && companies.length > 0) {
            const selectedName = selectedCompanyNames[0];
            const company = companies.find((c) => c.entiteitNaam === selectedName);
            hasCompanyId = !!(company === null || company === void 0 ? void 0 : company.entiteitId);
        }
        if (!hasCompanyId && selectedCompanyId) {
            hasCompanyId = true;
        }
        // We're waiting if we don't have companyId yet
        waitingForCompanyId = !hasCompanyId;
    }
    // Show loading state if waiting and no data yet
    React.useEffect(() => {
        if (waitingForCompanyId && snapshotsData.length === 0) {
            setIsSearching(true);
        }
        else if (!waitingForCompanyId && snapshotsData.length > 0) {
            // Only set to false if we have data and not waiting
            // Keep true if still fetching
            if (!snapshotsLoading) {
                setIsSearching(false);
            }
        }
    }, [waitingForCompanyId, snapshotsData.length, snapshotsLoading]);
    const showContentLoader = isSearching || snapshotsLoading || waitingForCompanyId;
    // Restore the grid's scroll position once the rows for the restored page
    // have actually rendered, then keep persisting it as the user scrolls.
    // The DataGrid still needs a couple of frames after the loader clears to
    // finish measuring/virtualizing row heights â€” setting scrollTop before that
    // gets silently clamped back to 0 because scrollHeight is still 0, so the
    // restore is deferred past layout with rAF (with a couple of retries in
    // case layout is still settling).
    useEffect(() => {
        if (hasRestoredScrollRef.current) {
            return;
        }
        if (showContentLoader || snapshotsData.length === 0) {
            return;
        }
        const saved = sessionStorage.getItem("project-scrollTop");
        if (!saved) {
            hasRestoredScrollRef.current = true;
            return;
        }
        const target = Number(saved);
        let cancelled = false;
        let attempts = 0;
        const tryRestore = () => {
            var _a;
            if (cancelled) {
                return;
            }
            const scroller = (_a = gridWrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(".MuiDataGrid-virtualScroller");
            attempts += 1;
            if (scroller &&
                (scroller.scrollHeight > scroller.clientHeight || attempts >= 5)) {
                scroller.scrollTop = target;
                hasRestoredScrollRef.current = true;
                return;
            }
            requestAnimationFrame(tryRestore);
        };
        requestAnimationFrame(tryRestore);
        return () => {
            cancelled = true;
        };
    }, [showContentLoader, snapshotsData]);
    useEffect(() => {
        var _a;
        const scroller = (_a = gridWrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(".MuiDataGrid-virtualScroller");
        if (!scroller) {
            return;
        }
        const handleScroll = () => {
            sessionStorage.setItem("project-scrollTop", String(scroller.scrollTop));
        };
        scroller.addEventListener("scroll", handleScroll, { passive: true });
        return () => scroller.removeEventListener("scroll", handleScroll);
    }, [snapshotsData]);
    const columns = useMemo(() => createColumns(debouncedSearchTerm, isSearchResult, unitType, handleToggleExcluded, canUpdate("Projects")), [
        debouncedSearchTerm,
        isSearchResult,
        unitType,
        canUpdate,
        handleToggleExcluded,
    ]);
    // Open a project and remember the rows currently on screen, so the detail
    // page can offer Vorige/Volgend project.
    const openProjectDetail = React.useCallback((row) => {
        const companyId = getSelectedCompanyIds()[0] || "";
        const aanneemsom = debouncedAanneemsom;
        // Remember this page plus the request behind it, so the detail page can
        // fetch the neighbouring page when the user walks off either edge.
        rememberProjectNavList({
            entries: (snapshotsData !== null && snapshotsData !== void 0 ? snapshotsData : [])
                .filter((r) => !(r === null || r === void 0 ? void 0 : r.isTotals))
                .map((r) => toProjectNavEntry(r, companyId, aanneemsom)),
            page: page + 1, // grid page is 0-based, Pagination.page is 1-based
            pageSize,
            totalCount: totalRowCount,
            request: snapshotRequest,
            companyId,
            aanneemsom,
        });
        navigate(buildProjectDetailUrl(row, String(row.parentProjectId || getSnapshotRowId(row)), companyId, aanneemsom));
    }, [
        snapshotsData,
        debouncedAanneemsom,
        getSelectedCompanyIds,
        navigate,
        page,
        pageSize,
        totalRowCount,
        snapshotRequest,
    ]);
    // Format config month to "December 2025" format
    const formatConfigMonth = (configMonth) => {
        const date = parseMonth(configMonth);
        if (!date)
            return configMonth;
        const monthNames = [
            "januari",
            "februari",
            "maart",
            "april",
            "mei",
            "juni",
            "juli",
            "augustus",
            "september",
            "oktober",
            "november",
            "december",
        ];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();
        return `${month} ${year}`;
    };
    const forecastDataMonth = companyMonthConfigs && companyMonthConfigs.length > 0
        ? formatConfigMonth(companyMonthConfigs[0].configMonth)
        : "";
    return (React.createElement(Box, null,
        React.createElement(PageHeader, { title: "Projecten", subtitle: forecastDataMonth
                ? `Forecast Data Month: ${forecastDataMonth}`
                : undefined, actions: React.createElement(React.Fragment, null, !isMobile && (React.createElement(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                } },
                React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), sx: { width: 173, flexShrink: 0 }, endIcon: searchTerm ? (React.createElement(Box, { component: "span", onClick: () => setSearchTerm(""), sx: {
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                        } },
                        React.createElement(CloseSVGIcon, null))) : (React.createElement(SearchSVGIcon, null)) }),
                React.createElement(FormControl, { size: "medium", sx: { width: 173, flexShrink: 0 } },
                    React.createElement(Select, { size: "medium", id: "unit-select", value: unitType, displayEmpty: true, IconComponent: ChevronDownIcon, onChange: (e) => handleUnitTypeChange(e.target.value), sx: {
                            borderRadius: "40px",
                            "& .MuiSelect-select": {
                                padding: "12px 44px 12px 16px",
                            },
                        } },
                        React.createElement(MenuItem, { value: "K" }, "K (Duizend)"),
                        React.createElement(MenuItem, { value: "full" }, "Volledig")))))) }),
        isLoadingPermissions ? (React.createElement(Box, { sx: { p: 4, textAlign: "center" } },
            React.createElement(CircularProgress, null))) : isPermissionAuthError ? (React.createElement(Navigate, { to: "/login", replace: true })) : !canRead("Projects") ? (React.createElement(NoModuleAccess, { message: "U heeft geen toegang tot de projecten module" })) : (React.createElement(React.Fragment, null,
            totalsRow && (React.createElement(Card, { sx: {
                    mt: 0.5,
                    px: { xs: 2, md: 1 },
                    py: { xs: 1.5, md: 1 },
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
                    overflowX: "auto",
                } },
                React.createElement(Stack, { direction: { xs: "column", md: "row" }, spacing: { xs: 1.25, md: 0 }, divider: React.createElement(Divider, { orientation: "vertical", flexItem: true, sx: { display: { xs: "none", md: "block" } } }), sx: {
                        alignItems: { xs: "flex-start", md: "center" },
                        minWidth: { md: 760 },
                    } },
                    React.createElement(TotalItem, { label: "Aanneemsom", value: formatTotalValue(totalsRow.aanneemsom || 0, unitType), icon: React.createElement(Moneys, { variant: "Bulk", size: "22px", color: "#86b845" }) }),
                    React.createElement(TotalItem, { label: "Forecast Marge", value: formatTotalValue(totalsRow.forecastMargin || 0, unitType), icon: React.createElement(StatusUp, { variant: "Bulk", size: "22px", color: theme.palette.warning.dark }) }),
                    React.createElement(TotalItem, { label: "Gefactureerd PTD", value: formatTotalValue(totalsRow.gefactureerdPTD || 0, unitType), icon: React.createElement(MoneyRecive, { variant: "Bulk", size: "22px", color: theme.palette.info.main }) }),
                    React.createElement(TotalItem, { label: "Kosten PTD", value: formatTotalValue(totalsRow.kostenPTD || 0, unitType), icon: React.createElement(MoneyTick, { variant: "Bulk", size: "22px", color: "#1255a1" }) }),
                    React.createElement(TotalItem, { label: "OHW", value: formatTotalValue(totalsRow.ohw || 0, unitType), icon: React.createElement(Folder, { variant: "Bulk", size: "22px", color: theme.palette.primary.main }) })))),
            React.createElement(Box, { sx: {
                    mt: 1.5,
                    mb: 1,
                } },
                React.createElement(Tabs, { value: statusTab, onChange: handleStatusTabChange, "aria-label": "project status tabs", variant: "scrollable", sx: {
                        backgroundColor: "transparent",
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: "8px" },
                        "& .MuiTab-root": {
                            minHeight: 36,
                            height: 36,
                            borderRadius: "36px",
                            px: 1.5,
                            backgroundColor: "#f4f6f8",
                            color: "#454f5b",
                            fontSize: 14,
                            fontWeight: 500,
                            textTransform: "none",
                        },
                        "& .MuiTab-root.Mui-selected": {
                            backgroundColor: "#00244d",
                            color: "#ffffff",
                        },
                    }, slotProps: {
                        indicator: { sx: { display: "none" } },
                    } },
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Alles"),
                            React.createElement(Chip, { label: (_a = totalProjectsCount !== null && totalProjectsCount !== void 0 ? totalProjectsCount : totalRowCount) !== null && _a !== void 0 ? _a : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.grey[300],
                                    color: theme.palette.text.primary,
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "all" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Nieuw"),
                            React.createElement(Chip, { label: newProjectsCount !== null && newProjectsCount !== void 0 ? newProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.info.light,
                                    color: "#161c24",
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "new" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Afgerond"),
                            React.createElement(Chip, { label: afgerondProjectsCount !== null && afgerondProjectsCount !== void 0 ? afgerondProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.success.light,
                                    color: "#161c24",
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "afgerond", sx: {
                            "&.Mui-selected": {
                                backgroundColor: "success.light",
                                color: "#161c24",
                            },
                        } }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Nog te doen"),
                            React.createElement(Chip, { label: inProgressProjectsCount !== null && inProgressProjectsCount !== void 0 ? inProgressProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: "#fdf0b0",
                                    color: "#161c24",
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "inprogress" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Massa afgerond"),
                            React.createElement(Chip, { label: bulkClosedProjectsCount !== null && bulkClosedProjectsCount !== void 0 ? bulkClosedProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.primary[20],
                                    color: "#161c24",
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "bulkclosed" }),
                    React.createElement(Tab, { label: React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                            React.createElement("span", null, "Niet waarderen"),
                            React.createElement(Chip, { label: excludedProjectsCount !== null && excludedProjectsCount !== void 0 ? excludedProjectsCount : 0, size: "small", sx: {
                                    height: 20,
                                    minWidth: 20,
                                    backgroundColor: theme.palette.error.light,
                                    color: "#161c24",
                                    borderRadius: "500px",
                                    pointerEvents: "none",
                                } })), value: "excluded" }))),
            isMobile ? (React.createElement(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                } },
                React.createElement(Box, { sx: {
                        display: "flex",
                        gap: 1,
                        mb: 1,
                        alignItems: "center",
                    } },
                    React.createElement(FilteredInput, { placeholder: "Zoeken", fullWidth: true, sx: { width: "100%" }, value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), endIcon: searchTerm ? (React.createElement(Box, { component: "span", onClick: () => setSearchTerm(""), sx: {
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                            } },
                            React.createElement(CloseSVGIcon, null))) : (React.createElement(SearchSVGIcon, null)) }),
                    React.createElement(IconButton, { onClick: () => setFilterDrawerOpen(true), sx: {
                            flexShrink: 0,
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        } },
                        React.createElement(FilterIcon, null))),
                React.createElement(BDrawer, { open: filterDrawerOpen, onClose: () => setFilterDrawerOpen(false), height: "auto", p: 3 },
                    React.createElement(Box, { sx: {
                            mt: 2,
                        } },
                        React.createElement(FormControl, { size: "small", fullWidth: true, sx: { mb: 2 } },
                            React.createElement(InputLabel, { id: "unit-select-label-mobile" }, "Eenheid"),
                            React.createElement(Select, { labelId: "unit-select-label-mobile", id: "unit-select-mobile", value: unitType, label: "Eenheid", onChange: (e) => handleUnitTypeChange(e.target.value) },
                                React.createElement(MenuItem, { value: "K" }, "K (Duizend)"),
                                React.createElement(MenuItem, { value: "M" }, "M (Miljoen)"),
                                React.createElement(MenuItem, { value: "full" }, "Volledig"))),
                        React.createElement(FilteredInput, { placeholder: "Aanneemsom", type: "number", value: aanneemsom, onChange: (e) => setAanneemsom(e.target.value), sx: { width: "100%" } }))),
                showContentLoader ? (React.createElement(Box, { sx: { display: "grid", gap: 2, p: 0 } }, Array.from({ length: 6 }).map((_, index) => (React.createElement(Box, { key: index, sx: {
                        p: 3,
                        backgroundColor: "background.paper",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                    } },
                    React.createElement(Skeleton, { variant: "text", width: "70%", height: 32, sx: { mb: 1 } }),
                    React.createElement(Skeleton, { variant: "text", width: "50%", height: 20, sx: { mb: 0.75 } }),
                    React.createElement(Skeleton, { variant: "text", width: "60%", height: 36, sx: { mb: 0.5 } }),
                    React.createElement(Skeleton, { variant: "rounded", width: "100%", height: 20, sx: { borderRadius: 1 } }),
                    React.createElement(Skeleton, { variant: "text", width: "40%", height: 20, sx: { mt: 0.75 } })))))) : (React.createElement(React.Fragment, null, snapshotsData === null || snapshotsData === void 0 ? void 0 :
                    snapshotsData.map((row, index) => {
                        var _a;
                        return (React.createElement(ProjectCard, { key: getSnapshotRowId(row, index), onClick: () => openProjectDetail(row), projectName: formatProjectName(row.parentProjectDescription || "N/A", debouncedSearchTerm, isSearchResult, row.tooltipPath), projectStatus: (_a = row.projectStatus) !== null && _a !== void 0 ? _a : row.ProjectStatus, projectNumber: row.parentProjectNumber, projectLeadName: row.projectLeadName, aanneemsom: row.aanneemsom, forecastMargin: row.forecastMargin, omzet: row.omzet, percent: row.valuationLevel != null && row.valuationLevel !== 1
                                ? null
                                : row.afgerondPercent, searchTerm: debouncedSearchTerm, isSearchResult: isSearchResult }));
                    }),
                    React.createElement(CardPagination, { count: totalRowCount, page: page, pageSize: pageSize, onPageChange: (newPage) => setPage(newPage), onPageSizeChange: (newSize) => setPageSize(newSize), pageSizeOptions: [5, 10, 15, 20] }))))) : (React.createElement(React.Fragment, null,
                (filterModel.items.filter((i) => { var _a; return !String((_a = i.id) !== null && _a !== void 0 ? _a : "").startsWith("status-tab-"); }).length > 0 ||
                    searchTerm) && (React.createElement(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pb: 1,
                        mb: 0,
                    } },
                    React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 500, color: "#161c24" } },
                        filterModel.items.filter((i) => { var _a; return !String((_a = i.id) !== null && _a !== void 0 ? _a : "").startsWith("status-tab-"); }).length + (searchTerm ? 1 : 0),
                        " ",
                        "filters toegepast"),
                    React.createElement(Button, { variant: "contained", size: "small", onClick: handleClearAll, startIcon: React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M18 6L6 18", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }),
                            React.createElement("path", { d: "M6 6L18 18", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })), sx: {
                            textTransform: "none",
                            color: "#161c24",
                            fontSize: 14,
                            fontWeight: 500,
                            backgroundColor: "rgba(145, 158, 171, 0.08)",
                            borderRadius: "40px",
                            px: 1.5,
                            height: 36,
                            boxShadow: "none",
                            "&:hover": {
                                backgroundColor: "rgba(145, 158, 171, 0.16)",
                                boxShadow: "none",
                            },
                        } }, "Filters wissen"))),
                React.createElement(Box, { ref: setGridWrapper },
                    React.createElement(PrimaryGrid, { apiRef: gridApiRef, loading: showContentLoader, disableColumnFilter: false, disableColumnMenu: false, disableColumnSelector: true, filterMode: "server", paginationMode: "server", 
                        // Measured from where the card actually starts, so the filter
                        // bar appearing or disappearing needs no second constant.
                        gridHeight: gridHeight, sortModel: sortModel, onSortModelChange: handleSortModelChange, filterModel: filterModel, onFilterModelChange: setFilterModel, rows: snapshotsData, columns: columns, pagination: true, heightVariant: "full", getRowId: (row) => getSnapshotRowId(row), paginationModel: { page, pageSize }, onPaginationModelChange: ({ page: newPage, pageSize: newPageSize, }) => {
                            // Prevent page reset to 0 when sorting (DataGrid tries to reset page on sort)
                            if (isSortingRef.current && newPage === 0 && page !== 0) {
                                return;
                            }
                            setPage(newPage);
                            setPageSize(newPageSize);
                        }, pageSizeOptions: [10, 20, 30, 50, 100], rowCount: totalRowCount, slotProps: {
                            filterPanel: {
                                disableAddFilterButton: false,
                                disableRemoveAllButton: false,
                                columnsSort: "asc",
                                filterFormProps: {
                                    columnInputProps: {
                                        disabled: true,
                                        readOnly: true,
                                    },
                                    // The delete icon is styled/positioned to look like a
                                    // "close" (X) button (see filterFormDeleteIcon sx below),
                                    // so users click it expecting to just close the panel.
                                    // By default it deletes the filter item instead â€” override
                                    // it to close the panel and keep the applied filter value.
                                    deleteIconProps: {
                                        onClick: (event) => {
                                            var _a;
                                            event.stopPropagation();
                                            (_a = gridApiRef.current) === null || _a === void 0 ? void 0 : _a.hideFilterPanel();
                                        },
                                    },
                                },
                            },
                        }, sx: {
                            "& .MuiDataGrid-filterForm": {
                                "& .MuiDataGrid-filterFormDeleteIcon": {
                                    order: 999,
                                    marginLeft: "auto",
                                },
                            },
                            // Completely hide the column selector dropdown
                            "& .MuiDataGrid-filterFormColumnInput": {
                                display: "none !important",
                                visibility: "hidden !important",
                                width: 0,
                                margin: 0,
                                padding: 0,
                                "& *": {
                                    display: "none !important",
                                },
                            },
                            // Style the value input as a search field
                            "& .MuiDataGrid-filterFormValueInput": {
                                flex: 1,
                                "& .MuiInputBase-root": {
                                    borderRadius: "40px",
                                    height: "45px",
                                    padding: "12px 16px",
                                },
                            },
                        }, onRowClick: (params) => openProjectDetail(params.row) }))))))));
};
